import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';

import {
  MarketplaceAvailableSoonCountDocument,
  MarketplaceAvailableSoonPubliclyListedCountDocument,
  type MarketplaceSearchAreaInput,
} from '../browse/__generated__/marketplace-browse.zeus';
import { viewportSearchArea } from './viewportSearchArea';

/** The Zeus search-area input accepts exactly one scope; pick in precedence order. */
export const toSearchAreaInput = (
  location: Pick<PropertySearchQuery, 'market' | 'vertices' | 'zoneIds'>,
): MarketplaceSearchAreaInput | null => {
  if (location.zoneIds?.length) {
    return { zoneIds: location.zoneIds };
  }
  if (location.vertices?.length) {
    return viewportSearchArea(location.vertices);
  }
  return location.market ? { market: location.market } : null;
};

type UseAvailableSoonCountOptions = {
  where: MarketplaceSearchAreaInput | null;
};

/**
 * Available-soon homes in the search area, independent of the filtered result
 * set. Drives whether the inventory segment control renders. Null from the
 * server (failed count read) hides the control.
 */
export const useAvailableSoonCount = ({ where }: UseAvailableSoonCountOptions) =>
  useQuery({
    queryKey: ['marketplace', 'availableSoonCount', where],
    queryFn: async ({ signal }) => {
      if (!where) {
        throw new Error('availableSoonCount requires a search area');
      }
      const data = await requestZeusFromBrowser(
        MarketplaceAvailableSoonCountDocument,
        { where },
        signal,
      );
      return data.marketplace.availableSoonCount;
    },
    enabled: where != null,
    staleTime: 60_000,
  });

/**
 * Available-soon homes in the search area whose buydirect channel is publicly
 * listed, independent of the filtered result set. Drives whether the
 * availability control may present checkboxes: an MLS-listed home still shown
 * as available soon is conflated data, so the control falls back to the
 * exclusive segment until the pool is clean. Null (failed read) also means
 * the exclusive control.
 */
export const useAvailableSoonPubliclyListedCount = ({ where }: UseAvailableSoonCountOptions) =>
  useQuery({
    queryKey: ['marketplace', 'availableSoonPubliclyListedCount', where],
    queryFn: async ({ signal }) => {
      if (!where) {
        throw new Error('availableSoonPubliclyListedCount requires a search area');
      }
      const data = await requestZeusFromBrowser(
        MarketplaceAvailableSoonPubliclyListedCountDocument,
        { where },
        signal,
      );
      return data.marketplace.availableSoonPubliclyListedCount;
    },
    enabled: where != null,
    staleTime: 60_000,
  });
